<script lang="ts">
	import classNames from 'classnames';
	import type { EmblaCarouselType } from 'embla-carousel';
	import { twMerge } from 'tailwind-merge';
	import type { HorizontalScrollState } from '#lib/utils/horizontalScrollHint/index.js';
	import { scrollByClamped } from '#lib/utils/scrollByClamped/scrollByClamped.js';
	import { ChevronLeftIcon } from '$global/assets/icons/ChevronLeftIcon';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';

	interface Props {
		target?: HTMLElement | null;
		emblaApi?: EmblaCarouselType;
		state: HorizontalScrollState;
		variant?: 'edge' | 'inline';
		dark?: boolean;
		previousLabel?: string;
		nextLabel?: string;
		class?: string;
		classes?: {
			previousButton?: string;
			nextButton?: string;
		};
	}

	let {
		target,
		emblaApi,
		state,
		variant = 'edge',
		dark = false,
		previousLabel = 'Show previous items',
		nextLabel = 'Show more items',
		class: className = '',
		classes = {},
	}: Props = $props();

	const scroll = (direction: -1 | 1) => {
		if (emblaApi) {
			if (direction === -1) emblaApi.scrollPrev();
			else emblaApi.scrollNext();
			return;
		}

		if (!target) return;

		scrollByClamped(
			target,
			direction * Math.max(160, target.clientWidth * 0.8)
		);
	};
</script>

{#if state.canScroll}
	{#if variant === 'edge'}
		<div
			class={classNames(
				'pointer-events-none absolute inset-0 z-10 flex items-stretch justify-between',
				className
			)}
			aria-hidden="false"
		>
			{#if !state.atStart}
				<button
					type="button"
					onclick={() => scroll(-1)}
					aria-label={previousLabel}
					class={twMerge(
						classNames(
							'pointer-events-auto flex min-w-11 items-center justify-start bg-gradient-to-r pr-12 pl-3',
							dark
								? 'text-gray-0 [&_path]:fill-gray-0 from-gray-900 via-gray-900 to-transparent'
								: 'from-white via-white to-transparent text-gray-900'
						),
						classes.previousButton
					)}
				>
					<!--
						Wrapped so a caller can make the button's gradient padding
						click-through (`pointer-events-none` on the button) without the
						chevron itself going dead — otherwise the fade area covers the
						adjacent tab and swallows its clicks.
					-->
					<span class="pointer-events-auto flex items-center">
						<ChevronLeftIcon class="h-4 w-4" />
					</span>
				</button>
			{:else}
				<span></span>
			{/if}

			{#if !state.atEnd}
				<button
					type="button"
					onclick={() => scroll(1)}
					aria-label={nextLabel}
					class={twMerge(
						classNames(
							'pointer-events-auto flex h-[calc(100%-2px)] min-w-11 items-center justify-end bg-gradient-to-l pr-4 pl-12',
							dark
								? 'text-gray-0 [&_path]:fill-gray-0 from-gray-900 via-gray-900 to-transparent'
								: 'from-white via-white to-transparent text-gray-900'
						),
						classes.nextButton
					)}
				>
					<span class="pointer-events-auto flex items-center">
						<ChevronRightIcon class="h-4 w-4" />
					</span>
				</button>
			{/if}
		</div>
	{:else}
		<div class={classNames('flex gap-2', className)}>
			<button
				type="button"
				onclick={() => scroll(-1)}
				disabled={state.atStart}
				aria-label={previousLabel}
				class={classNames(
					'flex h-9 w-9 items-center justify-center rounded border disabled:cursor-default disabled:opacity-30',
					dark
						? 'text-gray-0 [&_path]:fill-gray-0 border-gray-700 hover:border-gray-400'
						: 'border-gray-300 text-gray-900 hover:border-gray-500'
				)}
			>
				<ChevronLeftIcon class="h-4 w-4" />
			</button>

			<button
				type="button"
				onclick={() => scroll(1)}
				disabled={state.atEnd}
				aria-label={nextLabel}
				class={classNames(
					'flex h-9 w-9 items-center justify-center rounded border disabled:cursor-default disabled:opacity-30',
					dark
						? 'text-gray-0 [&_path]:fill-gray-0 border-gray-700 hover:border-gray-400'
						: 'border-gray-300 text-gray-900 hover:border-gray-500'
				)}
			>
				<ChevronRightIcon class="h-4 w-4" />
			</button>
		</div>
	{/if}
{/if}
